<template>
  <div class="game-list-scroll-page">
    <SiteHeader :config="configData" :query-string="queryString" />
    <!-- Larger Banner for Mobile when enabled -->
    <LargerBanner
      v-if="isEnableLargeBanner && getDeviceType() === 'mobile'"
      :slides="mobileBannerSlides"
      :image-url="imageUrl"
      :query-string="queryString"
    />
    <!-- Default Banner when LargerBanner is not enabled -->
    <Banner
      v-else-if="!isEnableLargeBanner"
      :slides="slides"
      :image-url="imageUrl"
      :query-string="queryString"
    />
    <section class="page-header">
      <div class="header-content">
        <h1>{{ $t('bestSellingGames') }}</h1>
        <button v-if="showViewAllLink && hasMoreGames" type="button" @click="expanded = true" class="view-all-link" aria-controls="homepage-games" :aria-expanded="String(expanded)">
          {{ $t('showMore') }}
        </button>
      </div>
    </section>
    <game-list-scroll
      id="homepage-games"
      :games="visibleGames"
      :image-url="imageUrl"
      :query-string="queryString"
    />
    <footer class="footer">
      <div class="copyright">copyright ©H365 All rights reserved.</div>
    </footer>
  </div>
</template>

<script>
import { configurationLocale } from '@/utils/navigation'
import SiteHeader from '@/components/SiteHeader.vue'
import GameListScroll from '@/components/GameListScroll.vue'
import LargerBanner from '@/components/LargerBanner.vue'
import Banner from '@/components/Banner.vue'
import { getCloudStorage } from '@/plugin/axios'
import qs from 'qs'

export default {
  name: 'GameListScrollPage',
  components: {
    SiteHeader,
    GameListScroll,
    LargerBanner,
    Banner
  },
  props: {
    isEnableLargeBanner: {
      type: Boolean,
      default: false
    },
    showViewAllLink: {
      type: Boolean,
      default: true
    },
    maxGames: {
      type: Number,
      default: 6
    }
  },
  data() {
    return {
      queryString: '',
      configData: {},
      games: [],
      expanded: false,
      slides: [],
      mobileBannerSlides: []

    }
  },
  async created() {
    this.queryString = window.location.search.slice(1)
    const query = qs.parse(this.queryString)
    const filename = query.utm_source ? query.utm_source : 'default'
    const url = encodeURIComponent(`config/minisite/${filename}.json`)
    try {
      const timestamp = Math.floor(Date.now() / (5 * 600))
      console.log('🌐 GameListScrollPage Request:', {
        url: url,
        fullUrl: `${process.env.VUE_APP_CLOUD_STORAGE_URL}/storage/v1/b/h365-landing-page/o/${url}`,
        params: { alt: 'media', t: timestamp },
        method: 'GET'
      })

      const { data } = await getCloudStorage.get(url, { params: { alt: 'media', t: timestamp } })
      if (data) {
        this.configData = data
        if (this.$i18n.messages[configurationLocale(data.lang)]) this.$i18n.locale = configurationLocale(data.lang)
        if (data?.games) {
          const width = this.isDesktop() ? 256 : 384
          this.games = data.games.map(item => ({
            ...item,
            image: `${width}/h365-landing-page/config/minisite/image/${item.image}`
          }))
        }
        if (data?.slides) {
          const width = this.isDesktop() ? 1360 : 428
          this.slides = data.slides.map(item => ({
            ...item,
            image: `${width}/h365-landing-page/config/minisite/image/${item.image}`
          }))
        }
        if (data?.mobile_banner_slides) {
          const width = 428 // Mobile only, fixed width
          this.mobileBannerSlides = data.mobile_banner_slides.map(item => ({
            ...item,
            image: `${width}/h365-landing-page/config/minisite/image/${item.image}`
          }))
        }
      }
    }
    catch (error) {
      console.error('❌ GameListScrollPage Error:', error)
      const defaultUrl = encodeURIComponent('config/minisite/default.json')

      const timestamp = Math.floor(Date.now() / (5 * 600))
      console.log('🔄 GameListScrollPage Fallback Request:', {
        url: defaultUrl,
        fullUrl: `${process.env.VUE_APP_CLOUD_STORAGE_URL}/storage/v1/b/h365-landing-page/o/${defaultUrl}`,
        params: { alt: 'media', t: timestamp },
        method: 'GET'
      })

      try {
        const { data } = await getCloudStorage.get(defaultUrl, { params: { alt: 'media', t: timestamp } })
        if (data) {
          this.configData = data
          if (this.$i18n.messages[configurationLocale(data.lang)]) this.$i18n.locale = configurationLocale(data.lang)
          if (data?.games) {
            const width = this.isDesktop() ? 256 : 384
            this.games = data.games.map(item => ({
              ...item,
              image: `${width}/h365-landing-page/config/minisite/image/${item.image}`
            }))
          }
          if (data?.slides) {
            const width = this.isDesktop() ? 1360 : 428
            this.slides = data.slides.map(item => ({
              ...item,
              image: `${width}/h365-landing-page/config/minisite/image/${item.image}`
            }))
          }
          if (data?.mobile_banner_slides) {
            const width = 428 // Mobile only, fixed width
            this.mobileBannerSlides = data.mobile_banner_slides.map(item => ({
              ...item,
              image: `${width}/h365-landing-page/config/minisite/image/${item.image}`
            }))
          }
        }
      }
      catch (fallbackError) {
        console.error('❌ GameListScrollPage Fallback Error:', fallbackError)
      }
    }
  },
  computed: {
    visibleGames() {
      return this.expanded || this.maxGames <= 0 ? this.games : this.games.slice(0, this.maxGames)
    },
    hasMoreGames() {
      return !this.expanded && this.maxGames > 0 && this.games.length > this.maxGames
    },
    imageUrl() {
      if (process.env.VUE_APP_IMAGE_URL)
        return process.env.VUE_APP_IMAGE_URL
      return ''
    }
  },
  methods: {
    isDesktop() {
      return document.body.getBoundingClientRect().width > 1024
    },
    getDeviceType() {
      const width = document.body.getBoundingClientRect().width
      if (width <= 768) return 'mobile'
      if (width > 1024) return 'desktop'
      return 'tablet'
    }
  }
}
</script>

<style lang="scss" scoped>
.game-list-scroll-page {
  min-height: 100vh;
  background-color: #1c1c1c;
  padding: 0;
}
.page-header {
  margin-top: 6px;
  margin-bottom: 4px;
  padding: 8px 20px 8px 20px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0px;
    left: 20px;
    right: 20px;
    height: 1px;
    background: rgb(255, 105, 180);
  }

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    max-width: 1200px;
    margin: 0 auto;
  }

  h1 {
    color: #ffffff;
    font-size: 32px;
    font-weight: bold;
    margin: 0;
    position: relative;
    display: inline-block;
  }

  .view-all-link {
    color: rgb(255, 105, 180);
    text-decoration: none;
    font-weight: bold;
    font-size: 16px;
    padding: 0px 12px;
    background: #000000;
    border: 2px solid rgb(255, 105, 180);
    border-radius: 20px;
    box-shadow: 0 0 8px rgba(255, 105, 180, 0.4);
    transition: all 0.3s ease;
    letter-spacing: 1px;

    &:hover {
      background: #000000;
      color: rgb(253, 72, 174);
      transform: scale(1.05);
      box-shadow: 0 0 12px rgba(255, 105, 180, 0.6);
      border-color: rgb(253, 72, 174);
    }
  }
}


@media (max-width: 768px) {
  .page-header {
    padding: 8px 15px 8px 15px;

    &::after {
      bottom: 0px;
      left: 15px;
      right: 15px;
    }

    .header-content {
      flex-direction: row;
      justify-content: space-between;
      align-items: flex-end;
      gap: 15px;
      text-align: left;
    }

    h1 {
      font-size: 28px;
    }

    .view-all-link {
      font-size: 14px;
      padding: 0px 12px;
      border-radius: 14px;
      flex-shrink: 0;
    }
  }


}

</style>